/**
 * Settings view (spec sections 33-38).
 *
 * Two rules this screen follows:
 *   1. There is no API-key field. Automated providers are configured with
 *      a Worker URL; the secret lives in the Worker environment.
 *   2. An operator-entered expiry date is labelled as a reminder, never as
 *      proof. Provider responses are the only authority.
 */

import { h } from '../../utils/dom.js';
import { formatBytes, formatDateTime } from '../../utils/format.js';
import {
  BRAND,
  RESEARCH,
  RESOLUTIONS,
  ASPECT_RATIOS,
  LOOP_MODES,
  MOODS,
  AUDIO_EFFECTS,
} from '../../core/config.js';
import { storage, getStorageDiagnostics, isUsingFallback } from '../../storage/db.js';
import { assetFootprint } from '../../storage/asset-store.js';
import { describeRecordingSupport, isWebCodecsAvailable } from '../../video/codec.js';
import { getLogEntries } from '../../utils/log.js';
import { card, button, note, kv, segmented, sectionHeading, chipRow, progressBar } from '../components/common.js';
import { toastOk, toastWarn, toastError } from '../components/toast.js';

export function renderSettings(ctx) {
  const { store, actions, registry, settings, sceneStore, promptLibrary } = ctx;
  const state = store.get();
  const root = h('div', { class: 'stack-lg' });
  const recording = describeRecordingSupport();
  const diagnostics = getStorageDiagnostics();

  root.append(sectionHeading('Settings', `${BRAND.appFull} v${BRAND.version}`));

  /* -------------------------------------------------- provider */

  const providerCards = h('div', { class: 'stack' });
  const statuses = state.providerStatus ?? {};
  for (const provider of registry.list()) {
    const status = statuses[provider.id]?.status ?? null;
    const caps = provider.capabilities;
    providerCards.append(
      h('div', { class: 'list__item' }, [
        h('div', { class: 'list__body' }, [
          h('div', { class: 'between' }, [
            h('div', { class: 'list__title' }, [provider.name]),
            h('span', { class: `chip ${caps.type === 'automated' ? 'chip--info' : caps.type === 'manual' ? 'chip--accent' : 'chip--ok'}` }, [caps.type]),
          ]),
          h('div', { class: 'tiny dim break' }, [provider.description]),
          chipRow([
            h('span', { class: 'chip' }, [caps.automated ? 'automated' : 'needs a human']),
            h('span', { class: 'chip' }, [caps.requiresApiKey ? 'server-side key' : 'no key']),
            h('span', { class: 'chip' }, [caps.costModel]),
            caps.browserAutomation ? h('span', { class: 'chip chip--danger' }, ['browser automation']) : null,
          ]),
          status?.detail ? h('div', { class: 'tiny' }, [status.detail]) : null,
        ]),
      ]),
    );
  }

  const workerField = h('div', { class: 'field' }, [
    h('label', { class: 'field__label', for: 'worker-url' }, ['Cloudflare Worker URL']),
    h('input', {
      id: 'worker-url',
      type: 'url',
      inputmode: 'url',
      placeholder: 'https://bex-loop-studio-worker.your-name.workers.dev',
      value: settings.workerUrl ?? '',
      onchange: (event) => actions.setSetting({ workerUrl: event.target.value.trim().replace(/\/+$/, '') }),
    }),
    h('div', { class: 'field__hint' }, [
      'The browser calls only this Worker. Your Gemini API key is a Worker secret and is never sent to the page.',
    ]),
  ]);

  root.append(
    card(
      [
        providerCards,
        h('div', { class: 'divider' }),
        workerField,
        h('div', { class: 'btn-row' }, [
          button('Check provider status', {
            kind: 'ghost',
            onClick: async () => {
              await actions.refreshProviderStatus();
              actions.render();
            },
          }),
          button('Open provider research', {
            kind: 'ghost',
            onClick: () =>
              actions.openSheet({
                title: 'Credential status',
                body: h('div', { class: 'stack' }, [
                  note(
                    'A manually entered expiration date is not proof that a credential has expired. BEX LOOP only reports what the provider or the Worker actually returns.',
                    'warn',
                  ),
                  kv([
                    ['Verification source', 'Cloudflare Worker /api/providers response'],
                    ['Last check', state.providerStatus?.['google-veo']?.status?.checkedAt ? formatDateTime(state.providerStatus['google-veo'].status.checkedAt) : 'never'],
                    ['Research verified on', RESEARCH.checkedOn],
                  ]),
                ]),
              }),
          }),
        ]),
      ],
      { title: 'Providers', step: 1 },
    ),
  );

  /* -------------------------------------------------- defaults */

  root.append(
    card(
      [
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default scene mode']),
          segmented(
            [
              { id: 'random', label: 'Random' },
              { id: 'mood', label: 'Mood' },
              { id: 'manual', label: 'Manual' },
              { id: 'auto', label: 'Auto' },
            ],
            settings.sceneMode,
            (id) => actions.setSetting({ sceneMode: id }),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default mood']),
          h(
            'select',
            { onchange: (event) => actions.setSetting({ mood: event.target.value }) },
            MOODS.map((mood) => h('option', { value: mood.id, selected: mood.id === settings.mood }, [mood.label])),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default resolution']),
          segmented(
            RESOLUTIONS.map((r) => ({ id: r.id, label: `${r.width}\u00d7${r.height}`, hint: r.tier })),
            settings.resolution,
            (id) => actions.setSetting({ resolution: id }),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default aspect ratio']),
          segmented(
            ASPECT_RATIOS.map((r) => ({ id: r.id, label: r.label })),
            settings.aspectRatio,
            (id) => actions.setSetting({ aspectRatio: id }),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default loop mode']),
          segmented(
            LOOP_MODES.map((m) => ({ id: m.id, label: m.label })),
            settings.loopMode,
            (id) => actions.setSetting({ loopMode: id }),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'field__label' }, ['Default audio effect']),
          h(
            'select',
            { onchange: (event) => actions.setSetting({ audioEffect: event.target.value }) },
            AUDIO_EFFECTS.map((effect) =>
              h('option', { value: effect.id, selected: effect.id === settings.audioEffect }, [effect.label]),
            ),
          ),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'between' }, [
            h('span', { class: 'field__label' }, ['Frame rate']),
            h('span', { class: 'tiny mono' }, [`${settings.fps} fps`]),
          ]),
          h('input', {
            type: 'range',
            min: '24',
            max: '60',
            step: '6',
            value: String(settings.fps),
            'aria-label': 'Frame rate',
            onchange: (event) => actions.setSetting({ fps: Number(event.target.value) }),
          }),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'between' }, [
            h('span', { class: 'field__label' }, ['Video bitrate']),
            h('span', { class: 'tiny mono' }, [`${settings.videoBitrateMbps} Mbps`]),
          ]),
          h('input', {
            type: 'range',
            min: '2',
            max: '20',
            step: '1',
            value: String(settings.videoBitrateMbps),
            'aria-label': 'Video bitrate',
            onchange: (event) => actions.setSetting({ videoBitrateMbps: Number(event.target.value) }),
          }),
        ]),
        h('div', { class: 'field' }, [
          h('div', { class: 'between' }, [
            h('span', { class: 'field__label' }, ['Audio-reactive strength']),
            h('span', { class: 'tiny mono' }, [`${settings.audioReactiveStrength}%`]),
          ]),
          h('input', {
            type: 'range',
            min: '0',
            max: '100',
            step: '5',
            value: String(settings.audioReactiveStrength),
            'aria-label': 'Audio reactive strength',
            onchange: (event) => actions.setSetting({ audioReactiveStrength: Number(event.target.value) }),
          }),
        ]),
        h('label', { class: 'switch' }, [
          h('span', { class: 'switch__text' }, [
            h('span', {}, ['Audio-reactive visuals']),
            h('span', { class: 'tiny dim' }, ['Scene elements respond subtly to the audio envelope.']),
          ]),
          h('input', {
            type: 'checkbox',
            checked: settings.audioReactive,
            onchange: (event) => actions.setSetting({ audioReactive: event.target.checked }),
          }),
        ]),
        h('label', { class: 'switch' }, [
          h('span', { class: 'switch__text' }, [
            h('span', {}, ['Monitor audio while rendering']),
            h('span', { class: 'tiny dim' }, ['Off by default. Rendering still captures the audio either way.']),
          ]),
          h('input', {
            type: 'checkbox',
            checked: settings.monitorAudio,
            onchange: (event) => actions.setSetting({ monitorAudio: event.target.checked }),
          }),
        ]),
      ],
      { title: 'Defaults', step: 2 },
    ),
  );

  /* -------------------------------------------------- storage */

  const usageBlock = h('div', { class: 'stack' }, [h('div', { class: 'tiny dim' }, ['Measuring...'])]);
  queueMicrotask(async () => {
    const [estimate, footprint] = await Promise.all([storage.estimate(), assetFootprint()]);
    usageBlock.replaceChildren(
      kv([
        ['Driver', isUsingFallback() ? `memory fallback (${diagnostics.fallbackReason})` : 'IndexedDB'],
        ['Imported clips', `${footprint.count} \u00b7 ${formatBytes(footprint.bytes)}`],
        ['Browser usage', estimate.supported ? formatBytes(estimate.usageBytes) : 'not reported'],
        ['Browser quota', estimate.supported ? formatBytes(estimate.quotaBytes) : 'not reported'],
        ['Persistent', estimate.persisted ? 'granted' : 'not granted'],
      ]),
      estimate.supported ? progressBar(Math.min(1, estimate.usagePercent / 100)) : null,
      note(
        'Video never goes into localStorage. Clips live in IndexedDB (or memory when IndexedDB is unavailable, e.g. some private windows).',
        'info',
      ),
    );
  });

  root.append(
    card(
      [
        usageBlock,
        h('div', { class: 'btn-row' }, [
          button('Request persistent storage', {
            kind: 'ghost',
            onClick: async () => {
              const granted = await storage.requestPersistence();
              if (granted) toastOk('The browser granted persistent storage.');
              else toastWarn('The browser declined. Data may be evicted under storage pressure.');
              actions.render();
            },
          }),
          button('Clear imported clips', {
            kind: 'danger',
            onClick: async () => {
              const ok = await actions.confirmSheet({
                title: 'Delete every imported clip?',
                message: 'Scene definitions stay, but their video assets are removed from this browser.',
                confirmLabel: 'Delete clips',
                kind: 'danger',
              });
              if (!ok) return;
              const { clearAssets } = await import('../../storage/asset-store.js');
              await clearAssets();
              for (const scene of sceneStore.all()) {
                if (scene.assetId) {
                  const updated = sceneStore.update(scene.id, {
                    assetType: scene.proceduralId ? 'procedural' : 'none',
                    assetId: null,
                    assetUrl: null,
                  });
                  await actions.persistScene(updated);
                }
              }
              await actions.loadLibrary();
              actions.render();
              toastOk('Imported clips removed.');
            },
          }),
        ]),
      ],
      { title: 'Storage', step: 3 },
    ),
  );

  /* -------------------------------------------------- diagnostics */

  const logEntries = getLogEntries().slice(-40).reverse();
  const capabilityReport = buildCapabilityReport(recording, diagnostics);

  root.append(
    card(
      [
        kv([
          ['Recording format', recording.chosen ? recording.chosen.label : 'unavailable'],
          ['Supported formats', recording.supportedLabels.join(', ') || 'none'],
          ['WebCodecs', isWebCodecsAvailable() ? 'available (future fast-export path)' : 'not available'],
          ['Scenes', String(sceneStore.size())],
          ['Prompts', String(promptLibrary.size())],
          ['Research verified', RESEARCH.checkedOn],
        ]),
        recording.warning ? note(recording.warning, 'warn') : null,
        h('div', { class: 'btn-row' }, [
          button('Copy diagnostics report', {
            kind: 'ghost',
            onClick: async () => {
              const ok = await actions.copyText(buildDiagnosticsReport(ctx, recording, diagnostics));
              if (ok) toastOk('Diagnostics copied to the clipboard.');
              else toastError('Clipboard unavailable.');
            },
          }),
        ]),
        h('details', {}, [
          h('summary', { class: 'small', style: { cursor: 'pointer' } }, [`Recent log (${logEntries.length})`]),
          h(
            'pre',
            { class: 'prompt-box', style: { maxHeight: '260px', marginTop: '8px' } },
            [
              logEntries
                .map((entry) => `${new Date(entry.time).toISOString().slice(11, 19)} ${entry.level.toUpperCase()} [${entry.scope}] ${entry.message}`)
                .join('\n') || 'no entries',
            ],
          ),
        ]),
        h('details', {}, [
          h('summary', { class: 'small', style: { cursor: 'pointer' } }, ['Capability report']),
          h('pre', { class: 'prompt-box', style: { maxHeight: '260px', marginTop: '8px' } }, [capabilityReport]),
        ]),
      ],
      { title: 'Diagnostics', step: 4 },
    ),
  );

  /* -------------------------------------------------- research */

  root.append(
    card(
      [
        h('h4', {}, ['Google Vids']),
        h('div', { class: 'stack' }, RESEARCH.googleVids.notes.map((line) => h('div', { class: 'tiny dim' }, [`\u2022 ${line}`]))),
        h('h4', { style: { marginTop: '12px' } }, ['Google Veo (Gemini API)']),
        h('div', { class: 'stack' }, RESEARCH.googleVeo.notes.map((line) => h('div', { class: 'tiny dim' }, [`\u2022 ${line}`]))),
        h('h4', { style: { marginTop: '12px' } }, ['Cloudflare']),
        h('div', { class: 'stack' }, RESEARCH.cloudflare.notes.map((line) => h('div', { class: 'tiny dim' }, [`\u2022 ${line}`]))),
        h('div', { class: 'divider' }),
        h('div', { class: 'tiny dim' }, [
          `Verified ${RESEARCH.checkedOn}. Sources: `,
          h('a', { href: RESEARCH.googleVeo.sources[0], target: '_blank', rel: 'noopener noreferrer', style: { color: 'var(--accent)' } }, ['Veo docs']),
          ' \u00b7 ',
          h('a', { href: RESEARCH.googleVids.sources[0], target: '_blank', rel: 'noopener noreferrer', style: { color: 'var(--accent)' } }, ['Vids announcement']),
          ' \u00b7 ',
          h('a', { href: RESEARCH.cloudflare.sources[0], target: '_blank', rel: 'noopener noreferrer', style: { color: 'var(--accent)' } }, ['Workers limits']),
        ]),
      ],
      { title: 'Verified platform facts', step: 5 },
    ),
  );

  /* -------------------------------------------------- danger zone */

  root.append(
    card(
      [
        note('Deleting removes imported clips, scene overrides, prompts, history and settings from this browser.', 'danger'),
        button('Delete all local data', { kind: 'danger', block: true, onClick: actions.clearAllData }),
      ],
      { title: 'Danger zone', step: 6 },
    ),
  );

  return root;
}

function buildCapabilityReport(recording, diagnostics) {
  const lines = [
    `userAgent: ${navigator.userAgent}`,
    `recording supported: ${recording.supportedLabels.join(' | ') || 'none'}`,
    `recording chosen: ${recording.chosen?.mime ?? 'none'}`,
    `webcodecs: ${isWebCodecsAvailable()}`,
    `mediaRecorder: ${typeof MediaRecorder !== 'undefined'}`,
    `audioContext: ${typeof (globalThis.AudioContext ?? globalThis.webkitAudioContext) !== 'undefined'}`,
    `canvasCaptureStream: ${typeof HTMLCanvasElement !== 'undefined' && typeof HTMLCanvasElement.prototype.captureStream === 'function'}`,
    `indexedDB: ${typeof indexedDB !== 'undefined'}`,
    `storage driver: ${diagnostics.driver}`,
    `deviceMemory: ${navigator.deviceMemory ?? 'unknown'}`,
    `hardwareConcurrency: ${navigator.hardwareConcurrency ?? 'unknown'}`,
  ];
  return lines.join('\n');
}

function buildDiagnosticsReport(ctx, recording, diagnostics) {
  const state = ctx.store.get();
  return [
    `BEX LOOP STUDIO ${BRAND.version}`,
    `generated: ${new Date().toISOString()}`,
    '',
    '--- environment ---',
    buildCapabilityReport(recording, diagnostics),
    '',
    '--- library ---',
    `scenes: ${ctx.sceneStore.size()} (ready: ${ctx.sceneStore.renderable().length})`,
    `prompts: ${ctx.promptLibrary.size()}`,
    `history entries: ${(state.history ?? []).length}`,
    '',
    '--- recording support ---',
    ...recording.supported.map((c) => `supported: ${c.mime}`),
    ...recording.unsupported.map((c) => `unsupported: ${c.mime}`),
    '',
    '--- settings ---',
    JSON.stringify(ctx.settings, null, 2),
  ].join('\n');
}
